// Patients (design/screens/patients.html): the search box — typeahead after two characters against /patients/search,
// debounced — and the filtered list with keyset paging, status chips and "New patient" (UC-PATIENTS-013, UC-PATIENTS-014).
// Filters, the order and the query live in the URL; saved views keep the filters and order in this browser (PAT-61).
// Keyboard: / focuses search, ↑↓ move, Enter opens, N is a new patient, Esc clears.
import { PATIENT_SEARCH_MIN_CHARS, PATIENT_STATUSES, type PatientConsentStatus, type PatientStatus } from "@visionx/contracts";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { patients, reference, type PatientListFilters } from "../api/endpoints.patients";
import { isApiError } from "../api/problem";
import { formatDate } from "../lib/format";
import { can, useSessionView } from "../session/store";
import { useWriteBlock } from "../shell/OfflineBanner";
import { Alert, Avatar, Checkbox, Empty, ErrorAlert, Field, Loading, Modal, Pager, Panel, Select, TextInput } from "../ui/bits";
import { Icon } from "../ui/icons";
import { NoAccessPanel, PageHead } from "./common";
import { GENDER_SHORT, MATCH_LABEL, PatientStatusChip, dobAge, mainCodeOf, patientInitials, useBranchNames, useDebounce, useEarlyEnter, writeTitle } from "./PatientsShared";
import { VIEW_NAME_MAX, loadViews, storeViews, viewParamsOf, withView, type SavedView } from "./PatientsViews";

const PAGE = 50;
const isTyping = (t: EventTarget | null): boolean => t instanceof HTMLElement && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
/** A control that Enter itself activates — a link, a button, a summary or anything a role makes one. */
const isControl = (t: EventTarget | null): boolean => t instanceof HTMLElement && t.closest("a[href], button, summary, [role='button'], [role='link'], [role='menuitem'], [role='option'], [role='tab'], [role='checkbox']") !== null;
const isStatus = (v: string | null): v is PatientStatus => (PATIENT_STATUSES as readonly string[]).includes(v ?? "");
/** The consent chips (PAT-74): the patients to ask again — consent outdated by a newer text — and those with none on file. */
const CONSENT_CHIPS: [PatientConsentStatus, string][] = [["Outdated", "Consent outdated"], ["None", "No consent"]];
const isConsentChip = (v: string | null): v is PatientConsentStatus => CONSENT_CHIPS.some(([c]) => c === v);
type Sort = NonNullable<PatientListFilters["sort"]>;
/** The orders of the list (UC-PATIENTS-014 step 2), as the select and the results' heading name them. */
const SORTS: [Sort, string, string][] = [["surname", "Surname", "by surname"], ["accountCode", "Account code", "by account code"], ["createdAt", "Newest first", "newest first"], ["lastVisitDate", "Last visit", "by last visit"]];
const isSort = (v: string | null): v is Sort => SORTS.some(([k]) => k === v);
const asDate = (v: string | null): string | undefined => (v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined);

export function PatientsList() {
  const s = useSessionView();
  const navigate = useNavigate();
  const block = useWriteBlock();
  const online = block === null;
  const canWrite = can(s, "patients.patient.write");
  const { name: branchName, all: allBranches } = useBranchNames();
  const [sp, setSp] = useSearchParams();
  const urlQ = sp.get("q") ?? "";
  const [typed, setTyped] = useState(urlQ);
  const [q, flush] = useDebounce(typed.trim(), 250);
  // kiosk, today, appointments: UC-PATIENTS-013 A7 (TC-PATIENTS-140) — the top bar's search reaches this list as
  // /patients?q=<term>, also while the list is open. The last term this screen wrote to the URL tells the two apart: a
  // different ?q= came from elsewhere and replaces the box.
  const pushed = useRef(urlQ);
  // end kiosk, today, appointments
  useEffect(() => {
    if (urlQ === q) return;
    pushed.current = q; // kiosk, today, appointments: remember this screen's own write (UC-PATIENTS-013 A7)
    setSp((p) => { const n = new URLSearchParams(p); if (q) n.set("q", q); else n.delete("q"); return n; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  // kiosk, today, appointments: UC-PATIENTS-013 A7 — follow an external ?q= change (the top bar's search)
  useEffect(() => {
    if (urlQ === pushed.current) return;
    pushed.current = urlQ;
    setTyped(urlQ);
  }, [urlQ]);
  // end kiosk, today, appointments
  const searching = q.length >= PATIENT_SEARCH_MIN_CHARS;
  const status = isStatus(sp.get("status")) ? sp.get("status") as PatientStatus : undefined;
  const incomplete = sp.get("incomplete") === "true";
  const includeArchived = sp.get("archived") === "true" || status === "Archived";
  const consent = isConsentChip(sp.get("consent")) ? sp.get("consent") as PatientConsentStatus : undefined;
  const dependants = sp.get("dependants") === "true";
  const sort: Sort = isSort(sp.get("sort")) ? sp.get("sort") as Sort : "surname";
  const createdFrom = asDate(sp.get("createdFrom"));
  const createdTo = asDate(sp.get("createdTo"));
  const visitFrom = asDate(sp.get("visitFrom"));
  const visitTo = asDate(sp.get("visitTo"));
  const filters: PatientListFilters = {
    status, branchId: sp.get("branch") ?? undefined, schemeId: sp.get("scheme") ?? undefined,
    missingEmail: sp.get("missingEmail") === "true" ? "true" : undefined,
    completeness: incomplete ? "Incomplete" : undefined, createdSource: incomplete ? "Import" : undefined,
    consentStatus: consent, hasDependents: dependants ? "true" : undefined,
    createdFrom, createdTo, lastVisitFrom: visitFrom, lastVisitTo: visitTo,
    includeArchived: includeArchived ? "true" : undefined,
    sort: sort === "surname" ? undefined : sort,
  };
  // A range that ends before it starts is refused here, before the server is asked (UC-PATIENTS-014 A2).
  const rangeError = createdFrom && createdTo && createdTo < createdFrom ? "Created: the end date is before the start date."
    : visitFrom && visitTo && visitTo < visitFrom ? "Last visit: the end date is before the start date." : null;
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  const list = useQuery({ queryKey: ["patients", "list", filters, cursor ?? ""], queryFn: () => patients.list(filters, { limit: PAGE, cursor }), enabled: !searching && rangeError === null });
  const search = useQuery({
    queryKey: ["patients", "search", q, filters.includeArchived ?? "", filters.branchId ?? ""],
    queryFn: () => patients.search(q, { limit: 50, includeArchived: filters.includeArchived, branchId: filters.branchId }), enabled: searching, placeholderData: keepPreviousData,
  });
  const schemes = useQuery({ queryKey: ["reference", "schemes"], queryFn: () => reference.schemes(), staleTime: 5 * 60_000 });
  const active = searching ? search : list;
  const rows = active.data?.data.items ?? [];
  const [sel, setSel] = useState(0);
  useEffect(() => { setSel(0); }, [active.data]);
  // Enter opens a row only from results that answer the box's text, never the previous term's still on screen (UC-PATIENTS-013 A9).
  const answer = active.isError ? { rows: [], complete: false } : active.isSuccess && !active.isPlaceholderData ? { rows, complete: !active.data.data.nextCursor } : null;
  const enter = useEarlyEnter(typed.trim(), q, flush, answer, (r) => navigate(`/patients/${r.id}`));
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const selRef = useRef(sel);
  selRef.current = sel;
  const input = useRef<HTMLInputElement>(null);
  const setParam = (k: string, v: string | null) => { setCursors([]); setSp((p) => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); return n; }, { replace: true }); };
  const clearFilters = () => { setCursors([]); setTyped(""); setSp(new URLSearchParams(), { replace: true }); };
  const hasFilters = [...sp.keys()].length > 0 || typed !== "";
  // Saved views (UC-PATIENTS-014 step 6; BR-PATIENTS-035): this browser's, for this user of this organisation.
  const owner = s ? { org: s.organisation.id, user: s.user.id } : null;
  const [views, setViews] = useState<SavedView[]>([]);
  useEffect(() => { setViews(owner ? loadViews(owner.org, owner.user) : []); }, [owner?.org, owner?.user]);
  const [savingView, setSavingView] = useState(false);
  const current = viewParamsOf(sp);
  const activeView = views.find((v) => v.params === current && current !== "") ?? null;
  const applyView = (name: string) => {
    const v = views.find((x) => x.name === name);
    if (!v) return;
    setCursors([]); setTyped("");
    setSp(new URLSearchParams(v.params), { replace: true });
  };

  // Capture phase: `/` must reach this screen's own search box before the shell focuses the top bar's (design: the list owns /).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      const typing = isTyping(e.target);
      if (e.key === "/" && !typing) { e.preventDefault(); e.stopPropagation(); input.current?.focus(); input.current?.select(); return; }
      if (typing && e.target !== input.current) return;
      const current = rowsRef.current;
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        // The arrows move the list's highlight and take the keyboard back to its box, so the Enter after them opens the highlighted
        // row — not the pager or chip the mouse last clicked, which keeps the focus (review, 5 Oct 2026). From a row's own link the
        // move starts at that row, never at a highlight elsewhere; a control outside this screen — the shell's — keeps its own keys
        // (review, 6 Oct 2026).
        const t = e.target instanceof Element ? e.target : null;
        const onControl = t !== null && t !== input.current && isControl(t);
        if (onControl && !t?.closest("#s-patients")) return;
        e.preventDefault();
        const row = onControl ? Number(t?.closest("tr[data-row]")?.getAttribute("data-row") ?? Number.NaN) : Number.NaN;
        if (onControl) input.current?.focus();
        const step = e.key === "ArrowDown" ? 1 : -1;
        setSel((i) => Math.min(Math.max((Number.isNaN(row) ? i : row) + step, 0), Math.max(current.length - 1, 0)));
      }
      else if (e.key === "Enter") {
        // Enter on a focused link or button is that control's: it opened the selected row instead — another patient's record,
        // with a Profile access entry against them (review, 5 Oct 2026). The list takes Enter from its box, or from the page.
        if (e.target !== input.current && isControl(e.target)) return;
        if (enter.answered()) { const r = current[selRef.current]; if (r) { e.preventDefault(); navigate(`/patients/${r.id}`); } }
        else if (e.target === input.current) { e.preventDefault(); enter.early(); }
      }
      else if (!typing && (e.key === "n" || e.key === "N")) { if (canWrite && online) { e.preventDefault(); navigate("/patients/new"); } }
      else if (e.key === "Escape") { if (typing) setTyped(""); else clearFilters(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, canWrite, online]);

  const chip = (on: boolean, label: string, onClick: () => void) => (
    <button type="button" className={`chip ${on ? "accent" : "neutral off"}`} aria-pressed={on} onClick={onClick}>{on && <Icon name="check" />}{label}</button>
  );
  const first = cursors.length * PAGE + 1;
  const denied = active.isError && isApiError(active.error) && active.error.code === "PERMISSION_DENIED";

  return (
    <section className="screen" id="s-patients">
      <div className="content">
        <PageHead
          title="Patients"
          sub={`Every patient of ${s?.organisation.tradingName ?? s?.organisation.legalName ?? "the practice"}${allBranches.length > 0 ? ` across ${allBranches.map((b) => b.name).join(", ")}` : ""}`}
          actions={<>
            {/* patients and settings wire-up (D64): the dependants-turned-18 worklist (UC-PATIENTS-011) */}
            <Link className="btn" to="/patients/turned-18">Dependants turned 18</Link>
            {/* the re-consent report (UC-GOVERNANCE-017 A4) reads with the list's own permission */}
            <Link className="btn" to="/patients/re-consent" title="The patients whose consent is missing or on replaced wording">Re-consent report</Link>
            {/* Deleted records (UC-PATIENTS-028 A3), for the roles that may delete — an Owner or Admin */}
            {can(s, "patients.patient.delete") && <Link className="btn" to="/patients/deleted">Deleted records</Link>}
            {canWrite && <button className="btn primary" type="button" disabled={!online} title={writeTitle(block)} onClick={() => navigate("/patients/new")}><Icon name="plus" />New patient</button>}
          </>}
        />
        <div className="toolbar">
          <div className="input w-search">
            <Icon name="search" />
            <input ref={input} type="search" aria-label="Search patients" placeholder="Name, account code, ID number, mobile…" autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} autoFocus />
            <kbd>/</kbd>
          </div>
          <Select aria-label="Branch" boxClass="w-sel" value={filters.branchId ?? ""} onChange={(e) => setParam("branch", e.target.value || null)}>
            <option value="">Branch · All branches</option>
            {allBranches.map((b) => <option key={b.id} value={b.id}>Branch · {b.name}</option>)}
          </Select>
          <Select aria-label="Medical aid" boxClass="w-sel" value={filters.schemeId ?? ""} onChange={(e) => setParam("scheme", e.target.value || null)}>
            <option value="">Medical aid · Any</option>
            {schemes.data?.data.items.map((sc) => <option key={sc.id} value={sc.id}>Medical aid · {sc.name}</option>)}
          </Select>
          <div className="chips">
            {chip(!!filters.missingEmail, "Missing email", () => setParam("missingEmail", filters.missingEmail ? null : "true"))}
            {chip(incomplete, "Incomplete import", () => setParam("incomplete", incomplete ? null : "true"))}
            {CONSENT_CHIPS.map(([c, label]) => <span key={c}>{chip(consent === c, label, () => setParam("consent", consent === c ? null : c))}</span>)}
            {chip(dependants, "Has dependants", () => setParam("dependants", dependants ? null : "true"))}
          </div>
        </div>
        <div className="toolbar">
          <span className="small muted">Status</span>
          <div className="chips" role="group" aria-label="Status">
            {chip(!status, "All", () => setParam("status", null))}
            {PATIENT_STATUSES.map((st) => <span key={st}>{chip(status === st, st, () => setParam("status", status === st ? null : st))}</span>)}
          </div>
          <Checkbox checked={includeArchived} disabled={status === "Archived"} onChange={(v) => setParam("archived", v ? "true" : null)}>Include archived</Checkbox>
          <span className="help">{includeArchived ? "Archived patients are shown" : "Archived patients are hidden"}</span>
          {hasFilters && <button className="btn sm right" type="button" onClick={clearFilters}>Clear filters</button>}
        </div>
        <div className="toolbar">
          <span className="small muted">Created</span>
          <TextInput type="date" aria-label="Created from" boxClass="w-date" value={createdFrom ?? ""} onChange={(e) => setParam("createdFrom", e.target.value || null)} mono />
          <span className="faint">–</span>
          <TextInput type="date" aria-label="Created to" boxClass="w-date" value={createdTo ?? ""} min={createdFrom} onChange={(e) => setParam("createdTo", e.target.value || null)} mono error={rangeError?.startsWith("Created") ?? false} />
          <span className="small muted">Last visit</span>
          <TextInput type="date" aria-label="Last visit from" boxClass="w-date" value={visitFrom ?? ""} onChange={(e) => setParam("visitFrom", e.target.value || null)} mono />
          <span className="faint">–</span>
          <TextInput type="date" aria-label="Last visit to" boxClass="w-date" value={visitTo ?? ""} min={visitFrom} onChange={(e) => setParam("visitTo", e.target.value || null)} mono error={rangeError?.startsWith("Last visit") ?? false} />
          <Select aria-label="Sort" boxClass="w-sort" value={sort} onChange={(e) => setParam("sort", e.target.value === "surname" ? null : e.target.value)}>
            {SORTS.map(([k, label]) => <option key={k} value={k}>Sort · {label}</option>)}
          </Select>
          <div className="row c right" style={{ gap: 8 }}>
            <Select aria-label="Saved views" boxClass="w-sel" value={activeView?.name ?? ""} onChange={(e) => applyView(e.target.value)}>
              <option value="">{views.length === 0 ? "Saved views · none yet" : "Saved views · choose"}</option>
              {views.map((v) => <option key={v.name} value={v.name}>View · {v.name}</option>)}
            </Select>
            <button className="btn sm" type="button" onClick={() => setSavingView(true)}>Save view…</button>
          </div>
        </div>
        {!searching && rangeError !== null ? <Alert tone="warn" role="alert"><b>{rangeError}</b> Correct the dates to see the list.</Alert> : <>
        {active.isPending && <Loading what="patients" />}
        {active.isError && (denied ? <NoAccessPanel page="Patients" error={active.error} /> : <ErrorAlert error={active.error} />)}
        {active.data && (
          <Panel title="Results"
            meta={searching ? `${rows.length} match${rows.length === 1 ? "" : "es"} for “${q}”${search.data?.data.nextCursor ? " · more exist, keep typing" : ""}` : rows.length === 0 ? "" : `${first}–${first + rows.length - 1}${list.data?.data.nextCursor ? " · more" : ""} · ${SORTS.find(([k]) => k === sort)![2]}`}
            head={<span className="meta right">Searches name, account code, ID number, mobile, card filing number and medical-aid member number</span>}
            tight
            foot={searching ? <span className="small faint">The first 50 matches are shown · type more to narrow</span> : <>
              <span className="small faint">Archived patients stay out of the list unless included · the home branch is an attribute, not a partition</span>
              <Pager label={`Page ${cursors.length + 1}`} hasPrev={cursors.length > 0} hasNext={!!list.data?.data.nextCursor} onPrev={() => setCursors((c) => c.slice(0, -1))} onNext={() => setCursors((c) => [...c, list.data!.data.nextCursor!])} />
            </>}>
            {rows.length === 0 ? (
              <Empty title={searching ? `No patient matches “${q}”.` : "No patients match these filters."} actions={canWrite && online && <Link className="btn primary" to="/patients/new">New patient</Link>}>
                {searching ? "Try the account code, ID number, mobile or card filing number; archived patients need the Include archived box." : hasFilters ? "Clear a filter or include archived patients." : "Register the first patient with New patient."}
              </Empty>
            ) : (
              <div className="tbl-scroll">
                <table>
                  <thead><tr><th>Patient</th><th>DOB / age</th><th>Gender</th><th>Home branch</th><th className="wide-only">Main account</th><th>Medical aid</th><th>Mobile</th><th className="wide-only">Card filing no</th><th>Status</th><th>Last visit</th></tr></thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const main = r.mainAccountId ? mainCodeOf(r.accountCode) : null;
                      const matched = searching ? r.matchedOn.filter((m) => m !== "surname" && m !== "firstName").map((m) => MATCH_LABEL[m] ?? m) : [];
                      return (
                        <tr key={r.id} data-row={i} className={`clickable${i === sel ? " sel" : ""}`} aria-selected={i === sel} onClick={() => navigate(`/patients/${r.id}`)}>
                          <td><div className="name-cell"><Avatar initials={patientInitials(r)} size="sm" /><div><Link className="nw" to={`/patients/${r.id}`} onClick={(e) => e.stopPropagation()}>{r.displayName}</Link><div className="sub mono">{r.accountCode}</div>{matched.length > 0 && <div className="sub">matched on {matched.join(", ")}</div>}</div></div></td>
                          <td className="nw">{dobAge(r.dateOfBirth, r.age)}</td>
                          <td>{GENDER_SHORT[r.gender]}</td>
                          <td>{branchName(r.homeBranchId) ?? <span className="faint">—</span>}</td>
                          <td className="wide-only">{r.mainAccountId ? <Link className="mono" to={`/patients/${r.mainAccountId}`} onClick={(e) => e.stopPropagation()}>{main ?? "main account"}</Link> : "Self"}</td>
                          <td>{r.schemeName ?? <span className="faint">—</span>}</td>
                          <td className="nw">{r.mobile ?? <span className="faint">—</span>}</td>
                          <td className="mono wide-only">{r.cardFilingNumber ?? <span className="faint">—</span>}</td>
                          <td><PatientStatusChip status={r.status} /></td>
                          <td className="nw">{r.lastVisitDate ? formatDate(r.lastVisitDate) : <span className="faint">—</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        )}
        </>}
        {savingView && owner && (
          <SaveViewModal views={views} params={current} onClose={() => setSavingView(false)}
            onStore={(next) => { if (!storeViews(owner.org, owner.user, next)) return false; setViews(next); return true; }} />
        )}
        <div className="kbd-hints">
          <span><kbd>↑</kbd><kbd>↓</kbd> move</span>
          <span><kbd>Enter</kbd> open</span>
          <span><kbd>/</kbd> search</span>
          {canWrite && <span><kbd>N</kbd> new patient</span>}
          <span><kbd>Esc</kbd> clear filters</span>
        </div>
      </div>
    </section>
  );
}

/**
 * Save view (UC-PATIENTS-014 step 6, A3, A4): names the list's current filters and order; a name already saved is replaced.
 * The saved views are listed with Delete. `onStore` answers false when the browser keeps nothing.
 */
function SaveViewModal({ views, params, onClose, onStore }: { views: SavedView[]; params: string; onClose: () => void; onStore: (next: SavedView[]) => boolean }) {
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const trimmed = name.trim();
  const replaces = views.find((v) => v.name.toLocaleLowerCase("en-ZA") === trimmed.toLocaleLowerCase("en-ZA"));
  const save = () => {
    if (params === "") { setError("Set a filter or an order first — the full list needs no view."); return; }
    if (trimmed === "") { setError("Name the view."); return; }
    if (onStore(withView(views, { name: trimmed, params }))) onClose(); else setFailed(true);
  };
  const remove = (v: SavedView) => { if (!onStore(views.filter((x) => x !== v))) setFailed(true); };
  return (
    <Modal title="Save view" meta="this browser · you only" onClose={onClose} onSubmit={save} footer={<>
      <button className="btn" type="button" onClick={onClose}>Cancel</button>
      <button className="btn primary" type="submit">{replaces ? "Replace view" : "Save view"}</button>
    </>}>
      {failed && <Alert tone="crit" role="alert"><b>This browser does not keep saved views.</b> Nothing was saved; the list works as before.</Alert>}
      <Field label="Name" required htmlFor="view-name" error={error ?? undefined} help={replaces ? `Replaces the saved view “${replaces.name}”.` : "Keeps the filters and the order — not the search text — in this browser, for you."}>
        <TextInput id="view-name" value={name} maxLength={VIEW_NAME_MAX} autoComplete="off" onChange={(e) => { setName(e.target.value); setError(null); }} error={!!error} />
      </Field>
      {views.length > 0 && (
        <div className="stack" style={{ gap: 6 }}>
          <div className="small strong muted">Saved views</div>
          {views.map((v) => (
            <div key={v.name} className="row c">
              <span className="grow">{v.name}</span>
              <button className="btn sm" type="button" aria-label={`Delete the view ${v.name}`} onClick={() => remove(v)}>Delete</button>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
